import React from 'react';
import {
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardBody,
  Icon,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiCalendar,
  FiUsers,
  FiTrendingUp,
} from 'react-icons/fi';
import { LoginRequestStats } from '../types';

interface LoginRequestStatsCardsProps {
  stats: LoginRequestStats;
  loading?: boolean;
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ElementType;
  colorScheme: string;
  helpText?: string;
  trend?: {
    value: number;
    type: 'increase' | 'decrease';
  };
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  colorScheme,
  helpText,
  trend,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Color mapping
  const colorMap = {
    blue: { bg: 'blue.50', color: 'blue.500', dark: { bg: 'blue.900', color: 'blue.300' } },
    green: { bg: 'green.50', color: 'green.500', dark: { bg: 'green.900', color: 'green.300' } },
    red: { bg: 'red.50', color: 'red.500', dark: { bg: 'red.900', color: 'red.300' } },
    orange: { bg: 'orange.50', color: 'orange.500', dark: { bg: 'orange.900', color: 'orange.300' } },
    purple: { bg: 'purple.50', color: 'purple.500', dark: { bg: 'purple.900', color: 'purple.300' } },
    yellow: { bg: 'yellow.50', color: 'yellow.500', dark: { bg: 'yellow.900', color: 'yellow.300' } },
  };

  const iconBg = useColorModeValue(
    colorMap[colorScheme as keyof typeof colorMap]?.bg || 'gray.50',
    colorMap[colorScheme as keyof typeof colorMap]?.dark.bg || 'gray.900'
  );
  
  const iconColor = useColorModeValue(
    colorMap[colorScheme as keyof typeof colorMap]?.color || 'gray.500',
    colorMap[colorScheme as keyof typeof colorMap]?.dark.color || 'gray.300'
  );

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
    >
      <CardBody>
        <HStack justify="space-between" align="start" mb={4}>
          <Stat>
            <StatLabel fontSize="sm" color="gray.500" fontWeight="medium">
              {label}
            </StatLabel>
            <StatNumber fontSize="2xl" fontWeight="bold" color={iconColor}>
              {value}
            </StatNumber>
            {helpText && (
              <StatHelpText fontSize="xs" color="gray.400">
                {helpText}
              </StatHelpText>
            )}
            {trend && (
              <StatHelpText>
                <StatArrow type={trend.type} />
                {trend.value}%
              </StatHelpText>
            )}
          </Stat>
          <Icon
            as={icon}
            boxSize={8}
            p={2}
            bg={iconBg}
            color={iconColor}
            borderRadius="lg"
          />
        </HStack>
      </CardBody>
    </Card>
  );
};

const LoginRequestStatsCards: React.FC<LoginRequestStatsCardsProps> = ({
  stats,
  loading = false,
}) => {
  const skeletonProps = loading ? { opacity: 0.6 } : {};

  const statCards = [
    {
      label: 'Pending Requests',
      value: stats.pending,
      icon: FiClock,
      colorScheme: 'yellow',
      helpText: 'Menunggu persetujuan',
    },
    {
      label: 'Approved Today',
      value: stats.todayApproved,
      icon: FiCheckCircle,
      colorScheme: 'green',
      helpText: 'Disetujui hari ini',
    },
    {
      label: 'Total Requests',
      value: stats.total,
      icon: FiUsers,
      colorScheme: 'blue',
      helpText: 'Total semua request',
    },
    {
      label: 'Today\'s Requests',
      value: stats.todayTotal,
      icon: FiCalendar,
      colorScheme: 'purple',
      helpText: 'Request hari ini',
    },
    {
      label: 'Rejected',
      value: stats.rejected,
      icon: FiXCircle,
      colorScheme: 'red',
      helpText: 'Total ditolak',
    },
    {
      label: 'Avg Response Time',
      value: stats.avgResponseTime > 0 ? `${stats.avgResponseTime}h` : '-',
      icon: FiTrendingUp,
      colorScheme: 'orange',
      helpText: 'Rata-rata waktu respon',
    },
  ];

  return (
    <SimpleGrid
      columns={{ base: 1, sm: 2, md: 3, lg: 6 }}
      spacing={4}
      {...skeletonProps}
    >
      {statCards.map((card) => (
        <StatCard
          key={card.label}
          label={card.label}
          value={card.value}
          icon={card.icon}
          colorScheme={card.colorScheme}
          helpText={card.helpText}
        />
      ))}
    </SimpleGrid>
  );
};

export default LoginRequestStatsCards;
